import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Avatar,
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Theme,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useThemeMode } from "contexts/ThemeContext";
import viewpaths from "global/constants/ViewPathConstants";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { animationStyle } from "utils/styles";
import darkLogo from "../../../assets/images/PanaceaInsightsLogoDark.svg";
import lightLogo from "../../../assets/images/PanaceaInsightsLogoLight.svg";
import { clearAuthUserIdFromStorage } from "../../../screens/Blogs/blogsUserPreferences";
import {
  logOutAction,
  selectAuthenticated,
  selectEmail,
  selectIsAdmin,
  selectUserName,
} from "../../../redux/authSlice";

const BRAND = "#f8481c";

const animation: any = makeStyles(animationStyle);

const authRequiredPaths = [
  viewpaths.dashboardViewPath,
  viewpaths.settingsViewPath,
  viewpaths.holidayCalenderViewPath,
];

const isAuthRequiredPath = (pathname: string) =>
  authRequiredPaths.some((p) => pathname.startsWith(p));

const TopNavSection = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { mode, toggleTheme } = useThemeMode();
  const muiTheme = useTheme();
  const currentPage = window.location.pathname.split("/")[1];
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const animationClasses = animation();
  const { t, i18n } = useTranslation();
  const authenticated = useSelector(selectAuthenticated);
  const isAdmin = useSelector(selectIsAdmin);
  const userName = useSelector(selectUserName);
  const email = useSelector(selectEmail);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };
  const handleLogout = () => {
    dispatch(logOutAction({} as any));
    clearAuthUserIdFromStorage();
    handleUserMenuClose();
    if (isAuthRequiredPath(location.pathname)) {
      history.push(viewpaths.landingViewPath);
    }
  };

  const displayName = userName?.trim() || email?.split("@")[0] || "User";
  const initial = (displayName.charAt(0) || "U").toUpperCase();

  useEffect(() => {
    i18n.changeLanguage("en");
  }, []);

  const navMenu = [
    {
      label: `${t("home")}`,
      url: viewpaths.landingViewPath,
    },
    {
      label: `${t("blogs")}`,
      url: viewpaths.blogsViewPath,
    },
    {
      label: `${t("contact")}`,
      url: viewpaths.contactViewPath,
    },
    ...(authenticated && isAdmin
      ? [
          {
            label: "Admin",
            url: viewpaths.adminDashboardViewPath,
          },
        ]
      : []),
  ];

  const handleNavClick = (menuItem: any) => {
    if (menuItem.isExternal) {
      window.open(menuItem.url, "_blank");
    } else {
      history.push(menuItem.url);
    }
  };

  return (
    <Stack
      alignItems="center"
      direction="row"
      width={"100%"}
      justifyContent={"space-between"}
      sx={{
        height: "100%",
      }}
    >
      {/* Logo - Left Corner */}
      <Box
        className={animationClasses.fadeInUp}
        onClick={() => history.push(viewpaths.landingViewPath)}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <img
          src={mode === "dark" ? darkLogo : lightLogo}
          alt="logo"
          height={55}
        />
      </Box>

      {/* Navigation Menu - Center */}
      {mdUp && (
        <Stack
          direction="row"
          spacing={4}
          alignItems="center"
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            height: "100%",
          }}
        >
          {navMenu.map((menuItem: any, index: number) => {
            const isActive = `/${currentPage}` === menuItem.url;
            return (
              <Box
                key={index}
                className={animationClasses.fadeInUp}
                sx={{
                  position: "relative",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  onClick={() => handleNavClick(menuItem)}
                  sx={{
                    fontFamily:
                      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    fontSize: "15px",
                    fontWeight: 400,
                    color: isActive
                      ? muiTheme.palette.text.primary
                      : muiTheme.palette.text.secondary,
                    cursor: "pointer",
                    textTransform: "none",
                    letterSpacing: "0.01em",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    pb: "2px",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: isActive ? "100%" : "0%",
                      height: "2px",
                      backgroundColor: muiTheme.palette.text.primary,
                      transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    },
                    "&:hover": {
                      color: muiTheme.palette.text.primary,
                      transform: "translateY(-2px)",
                      "&::after": {
                        width: "100%",
                      },
                    },
                  }}
                >
                  {menuItem.label}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Right Corner - Login & Theme Toggle */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
          height: "100%",
          ml: "auto",
        }}
      >
        {/* Theme Toggle Button */}
        <Box className={animationClasses.fadeInUp}>
          <IconButton
            onClick={toggleTheme}
            size="small"
            aria-label="toggle theme"
            sx={{
              color: muiTheme.palette.text.secondary,
              transition: "all 0.3s ease",
              "&:hover": {
                color: muiTheme.palette.text.primary,
                backgroundColor: muiTheme.palette.action.hover,
                transform: "scale(1.1)",
              },
              padding: "8px",
            }}
          >
            {mode === "dark" ? (
              <Brightness7Icon sx={{ fontSize: "20px" }} />
            ) : (
              <Brightness4Icon sx={{ fontSize: "20px" }} />
            )}
          </IconButton>
        </Box>

        {/* User menu (logged in) or Login button */}
        {mdUp && (
          <Box className={animationClasses.fadeInUp}>
            {authenticated ? (
              <>
                <Box
                  onClick={handleUserMenuOpen}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    padding: "6px 12px",
                    borderRadius: "999px",
                    cursor: "pointer",
                    color: muiTheme.palette.text.primary,
                    backgroundColor: alpha(
                      muiTheme.palette.text.primary,
                      muiTheme.palette.mode === "dark" ? 0.08 : 0.06
                    ),
                    border: `1px solid ${alpha(muiTheme.palette.divider, 0.5)}`,
                    transition: "all 0.25s ease",
                    "&:hover": {
                      backgroundColor: alpha(
                        muiTheme.palette.text.primary,
                        muiTheme.palette.mode === "dark" ? 0.12 : 0.1
                      ),
                      borderColor: alpha(BRAND, 0.5),
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "13px",
                      fontWeight: 600,
                      bgcolor: BRAND,
                      color: "#fff",
                    }}
                  >
                    {initial}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        maxWidth: { xs: 80, sm: 120 },
                      }}
                    >
                      {displayName}
                    </Typography>
                    {mdUp && email && (
                      <Typography
                        noWrap
                        variant="caption"
                        sx={{
                          display: "block",
                          color: muiTheme.palette.text.secondary,
                          fontSize: "11px",
                          maxWidth: 140,
                        }}
                      >
                        {email}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  onClick={handleUserMenuClose}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  PaperProps={{
                    elevation: 8,
                    sx: {
                      mt: 1.5,
                      minWidth: 220,
                      borderRadius: "12px",
                      overflow: "visible",
                      "&::before": {
                        content: '""',
                        display: "block",
                        position: "absolute",
                        top: 0,
                        right: 20,
                        width: 12,
                        height: 12,
                        bgcolor: "background.paper",
                        transform: "translateY(-50%) rotate(45deg)",
                        zIndex: 0,
                      },
                    },
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {userName || displayName}
                    </Typography>
                    {email && (
                      <Typography
                        variant="caption"
                        sx={{ color: muiTheme.palette.text.secondary }}
                      >
                        {email}
                      </Typography>
                    )}
                  </Box>
                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      gap: 1.5,
                      py: 1.25,
                      color: BRAND,
                      "&:hover": {
                        bgcolor: alpha(BRAND, 0.08),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0 }}>
                      <LogoutIcon sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    {t("logout") || "Log out"}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Box
                onClick={() => history.push(viewpaths.loginViewPath)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  color: muiTheme.palette.text.primary,
                  backgroundColor: "transparent",
                  border: `1px solid ${muiTheme.palette.divider}`,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    backgroundColor: muiTheme.palette.action.hover,
                    borderColor: muiTheme.palette.text.primary,
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <LoginIcon sx={{ fontSize: "18px" }} />
                <Typography
                  sx={{
                    fontFamily:
                      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    fontSize: "14px",
                    fontWeight: 500,
                    letterSpacing: "0.3px",
                    textTransform: "none",
                  }}
                >
                  {t("login") || "Login"}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default TopNavSection;
